import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { LintFinding } from '../videoLinter';

import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

function normalize(text: string): string {
    return text.trim().replace(/\s+/g, ' ').toLowerCase();
}

interface TextAssetLike {
    name: string;
    text: string;
    isEditable: boolean;
}

interface AssetContainer {
    getAssetsByType(type: string): TextAssetLike[];
}

/**
 * Warning: two text assets that are on screen at the same time say the same
 * thing. Assets are compared within each subscene (or the scene itself when it
 * has no extra subscenes) — the same text on *different* subscenes is fine,
 * since they show sequentially, and subscenes start life as copies.
 */
export function checkDuplicateText(luminary: Luminary, _getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];

    luminary.scenes.forEach((scene) => {
        const subscenes = scene.subscenes ?? [];
        const containers: { container: AssetContainer; label: string }[] =
            subscenes.length > 1
                ? subscenes.map((subscene, subsceneIndex) => ({
                      container: subscene as unknown as AssetContainer,
                      label: ` (subscene ${subsceneIndex})`,
                  }))
                : [{ container: scene as unknown as AssetContainer, label: '' }];

        containers.forEach(({ container, label }) => {
            const textAssets = container
                .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
                .filter((asset) => asset.isEditable && asset.text?.trim());

            const assetsByText = new Map<string, TextAssetLike[]>();
            textAssets.forEach((asset) => {
                const key = normalize(asset.text);
                const group = assetsByText.get(key) ?? [];
                group.push(asset);
                assetsByText.set(key, group);
            });

            assetsByText.forEach((group) => {
                if (group.length < 2) {
                    return;
                }
                const names = group.map((asset) => asset.name).join(', ');
                findings.push({
                    check: 'duplicate_text',
                    severity: 'warning',
                    sceneId: scene.id,
                    message: `Text assets ${names} on this scene${label} all say "${group[0].text.trim()}". Two boxes saying the same thing is almost always unintentional — clear one of them (set its text to "") or give it different content.`,
                });
            });
        });
    });

    return findings;
}
